import { TicketArticleModel, TicketDetailModel, TicketModel } from '../models/ticket.model';
import { ReservationDetailsModel } from '../models/reservation-details.model';
import {
  TicketArticleUiModel,
  TicketDetailUiModel,
  TicketReservationContextUiModel,
  TicketUiModel
} from '../ui-models/ticket.ui-model';

/**
 * Zammad's closed-like states. "merged" and "removed" are terminal too: an agent
 * cannot work either, so they read as closed rather than as live work.
 */
const CLOSED_STATES = ['closed', 'merged', 'removed'];

/** The integrator writes every handoff note as "Handed off from X to Y by Z." */
const HANDOFF_PREFIX = 'Handed off from ';

/**
 * An article body as plain text. Notes are plain text already; email articles
 * (once the CX mailbox is connected) arrive as HTML, and are reduced to their text
 * rather than injected. DOMParser does not run scripts or load images.
 */
export function articleText(body: string): string {
  if (!/<[a-z][\s\S]*>/i.test(body || '')) return body || '';
  const html = (body || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li)>/gi, '\n');
  const text = new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

class TicketMapper {
  public static mapToUI(model: TicketModel): TicketUiModel {
    return {
      id: model.id,
      number: model.number,
      title: model.title,
      state: model.state,
      isClosed: CLOSED_STATES.includes(model.state),
      priority: model.priority,
      team: model.team,
      ownerId: model.owner?.id,
      ownerName: model.owner?.name,
      categoryCode: model.category?.code,
      categoryLabel: model.category?.label,
      handling: model.category?.handling ?? undefined,
      emergency: model.emergency,
      nextOwner: model.nextOwner ?? undefined,
      reservationRef: model.reservationRef || undefined,
      unitRef: model.unitRef || undefined,
      contactRef: model.contactRef || undefined,
      createdAt: new Date(model.createdAt),
      updatedAt: new Date(model.updatedAt)
    };
  }

  public static mapToUIList(models: TicketModel[]): TicketUiModel[] {
    return (models ?? []).map(TicketMapper.mapToUI);
  }

  public static mapArticleToUI(model: TicketArticleModel): TicketArticleUiModel {
    return {
      id: model.id,
      author: model.author,
      body: articleText(model.body),
      internal: model.internal,
      // Older integrator responses carry no direction: an internal article is a note.
      direction: model.direction ?? (model.internal ? 'note' : 'out'),
      isHandoff: (model.body || '').startsWith(HANDOFF_PREFIX),
      createdAt: new Date(model.createdAt),
      attachments: model.attachments ?? []
    };
  }

  public static mapReservationToContext(
    model: ReservationDetailsModel
  ): TicketReservationContextUiModel {
    const guest = (model.guestsList ?? [])[0];
    return {
      id: model.id,
      status: String(model.status ?? ''),
      guestName:
        model.primaryGuestName ||
        [guest?.firstName, guest?.lastName].filter(Boolean).join(' ') ||
        '—',
      guestEmail: guest?.email || undefined,
      guestPhone: guest?.phoneNumber || undefined,
      propertyName: model.propertyName,
      unitName: model.unitName,
      checkIn: new Date(model.checkInDate),
      checkOut: new Date(model.checkOutDate),
      channel: model.channel ? String(model.channel) : model.source
    };
  }

  public static mapDetailToUI(model: TicketDetailModel): TicketDetailUiModel {
    return {
      ...TicketMapper.mapToUI(model),
      customer: model.customer ?? undefined,
      canReassign: !!model.canReassign,
      articles: (model.articles ?? []).map(TicketMapper.mapArticleToUI)
    };
  }
}

export default TicketMapper;
